import React, { useEffect, useState, useRef, useCallback } from 'react';
import { GoogleMap, Marker, OverlayView } from '@react-google-maps/api';

// 🖼️ Íconos propios
import amarillo from '../images/map marker/amarillo.png';
import azul from '../images/map marker/azul.png';
import celeste from '../images/map marker/celeste.png';
import morado from '../images/map marker/morado.png';
import naranjaFuerte from '../images/map marker/naranja fuerte.png';
import violeta from '../images/map marker/violeta.png';
import rojoItaliano from '../images/map marker/rojo italiano.png';
import verde from '../images/map marker/verde.png';
import verdeOscuro from '../images/map marker/verde oscuro.png';

const externalIcons = [
  amarillo, azul, celeste, morado,
  naranjaFuerte, violeta, rojoItaliano,
  verde, verdeOscuro,
];

const iconMappingExternosPorCiudad = {
  Arica: {
    'LBRJ-48': verde,
    'LBRH-86': verdeOscuro,
    'LBRJ-69': violeta,
  },
  Antofagasta: {
    'JFDG-74': amarillo,
    'KZKS-62': azul,
    'KZKS-50': celeste,
    'JYPC-60': morado,
    'LDPX-62': naranjaFuerte,
    'KZKS-36': violeta,
    'JYPC-61': rojoItaliano,
  },
};

const bubbleStyle = {
  backgroundColor: '#fff',
  border: '1px solid #ccc',
  borderRadius: '10px',
  padding: '4px 10px',
  fontSize: '13px',
  color: '#000',
  boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
  whiteSpace: 'nowrap',
  display: 'inline-block',
};

// 💬 Globo para pedidos
const pedidoBubbleStyle = {
  ...bubbleStyle,
  backgroundColor: '#fff9e6',
  border: '1px solid #f39c12',
};

// 🎨 Colores por móvil
const mobileColorMapping = {
  M01: '#27ae60',
  M02: '#2980b9',
  M03: '#16a085',
  M04: '#8e44ad',
  M05: '#e67e22',
  M06: '#9b59b6',
  M07: '#c0392b',
  M08: '#145a32',
};

const getPedidoColor = (pedido) => {
  const movil = pedido.campo_11?.trim();
  return mobileColorMapping[movil] || '#f39c12';
};

const mapOptions = {
  styles: [{
    featureType: 'poi.business',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  }],
};

// ✅ Clasificación por latitud con tolerancia ±1
const ciudadPorLatitud = (lat) => {
  const n = Number(lat);
  if (Number.isNaN(n)) return null;

  // Arica: -17, -18, -19 (rango [-19, -17])
  if (n >= -19 && n <= -17) return 'Arica';

  // Antofagasta: -22, -23, -24 (rango [-24, -22])
  if (n >= -24 && n <= -22) return 'Antofagasta';

  return null;
};

function GoogleCallCenterMap() {
  const [gpsCookie, setGpsCookie] = useState('');
  const [gpsExternos, setGpsExternos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [nextRefreshIn, setNextRefreshIn] = useState(60);
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);

  const [aricaCenter, setAricaCenter] = useState({ lat: -18.4583, lng: -70.3126 });
  const [antofaCenter, setAntofaCenter] = useState({ lat: -23.61, lng: -70.389 });

  const aricaMapRef = useRef(null);
  const antofaMapRef = useRef(null);

  const getPos = (str) => {
    if (!str) return null;
    const [lat, lng] = String(str).split(',').map(Number);
    return !isNaN(lat) && !isNaN(lng) ? { lat, lng } : null;
  };

  // ============= LOCALSTORAGE COOKIE =============
  useEffect(() => {
    const saved = localStorage.getItem('gpsCookie');
    if (saved) setGpsCookie(saved);
  }, []);

  useEffect(() => {
    if (gpsCookie) localStorage.setItem('gpsCookie', gpsCookie);
  }, [gpsCookie]);

  // ============= FETCH GPS EXTERNOS =============
  const fetchGPSExternos = useCallback(async () => {
    if (!gpsCookie) return;
    try {
      const fd = new FormData();
      fd.append('cookie', gpsCookie);

      const res = await fetch('https://pedidos.ayvgas.cl/gps/proxy_berman_gps.php', {
        method: 'POST',
        body: fd,
      });

      const data = await res.json();
      console.log('Respuesta API GPS:', data);

      // data[0] = móviles
      const raw = Array.isArray(data?.[0]) ? data[0] : [];

      setGpsExternos(() => {
        const usadosArica = new Set(Object.values(iconMappingExternosPorCiudad.Arica || {}));
        const usadosAntofa = new Set(Object.values(iconMappingExternosPorCiudad.Antofagasta || {}));

        return raw.map((v) => {
          const lat = v.up_latitud;
          const lng = v.up_longitud;

          // ✅ ciudad por latitud
          const ciudad = ciudadPorLatitud(lat);

          // patente/nombre (según lo que venga)
          const patente = v.vh_patente || v.vh_muestra || v.patente || '';
          let iconUrl = externalIcons[Math.floor(Math.random() * externalIcons.length)];

          // asignación de ícono consistente por ciudad/patente
          if (ciudad && iconMappingExternosPorCiudad[ciudad]?.[patente]) {
            iconUrl = iconMappingExternosPorCiudad[ciudad][patente];
          } else if (ciudad) {
            const usados = ciudad === 'Arica' ? usadosArica : usadosAntofa;
            const disp = externalIcons.find((ic) => !usados.has(ic));
            if (disp) { iconUrl = disp; usados.add(disp); }
          }

          return {
            ...v,
            ciudad,
            up_coordenadas: `${lat},${lng}`,
            iconUrl,
          };
        });
      });
    } catch (e) {
      console.error('Error fetchGPSExternos:', e);
      alert('Cookie inválida/expirada o respuesta no JSON.');
    }
  }, [gpsCookie]);

  // ============= FETCH PEDIDOS CALLCENTER =============
  const fetchPedidos = useCallback(async () => {
    try {
      const res = await fetch(
        `https://pedidos.ayvgas.cl/pedidos/pedido_callcenter.json?nocache=${Date.now()}`
      );
      const data = await res.json();

      let arr = [];
      if (Array.isArray(data)) arr = data;
      else if (data && typeof data === 'object') arr = Object.values(data);

      setPedidos(arr);

      console.log(
        'Pedidos recibidos:',
        Array.isArray(data) ? 'array' : typeof data,
        '→ normalizados:',
        arr.length
      );
    } catch (e) {
      console.error('Error fetchPedidos:', e);
    }
  }, []);

  // ============= EFECTO GPS (con contador) =============
  useEffect(() => {
    if (!gpsCookie) return;

    fetchGPSExternos();

    setNextRefreshIn(60);
    const countdownId = setInterval(() => {
      setNextRefreshIn((prev) => (prev <= 1 ? 60 : prev - 1));
    }, 1000);

    const intervalId = setInterval(() => {
      fetchGPSExternos();
      setNextRefreshIn(60);
    }, 60000);

    return () => {
      clearInterval(countdownId);
      clearInterval(intervalId);
    };
  }, [gpsCookie, fetchGPSExternos]);

  // ============= EFECTO PEDIDOS (independiente) =============
  useEffect(() => {
    fetchPedidos();
    const pedidosIntervalId = setInterval(fetchPedidos, 30000);
    return () => clearInterval(pedidosIntervalId);
  }, [fetchPedidos]);

  // ============= RENDER =============
  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 10, background: '#333333', display: 'flex', alignItems: 'center' }}>
        <label style={{ color: '#fff' }}>Cookie GPS Externo:&nbsp;</label>
        <input
          style={{ width: 400, marginRight: 10 }}
          value={gpsCookie}
          onChange={(e) => setGpsCookie(e.target.value)}
          placeholder="_gid=…; PHPSESSID=…"
        />
        <button onClick={fetchGPSExternos}>Cargar GPS</button>
        <span style={{ marginLeft: 20, color: '#fff' }}>
          Próx. actualización GPS: {nextRefreshIn}s
        </span>
      </div>

      <div style={{ flex: 1, display: 'flex' }}>
        {/* ================== MAPA ARICA ================== */}
        <GoogleMap
          mapContainerStyle={{ flex: 1 }}
          center={aricaCenter}
          zoom={13}
          options={mapOptions}
          onLoad={(m) => (aricaMapRef.current = m)}
          onIdle={() => {
            if (aricaMapRef.current) setAricaCenter(aricaMapRef.current.getCenter().toJSON());
          }}
          onClick={() => setSelectedPedidoId(null)}
        >
          {/* GPS externos en Arica (por latitud) */}
          {gpsExternos
            .filter((v) => v.ciudad === 'Arica')
            .map((v, i) => {
              const pos = getPos(v.up_coordenadas);
              if (!pos) return null;

              return (
                <React.Fragment key={`ar-gps-${i}`}>
                  <Marker
                    position={pos}
                    icon={{
                      url: v.iconUrl,
                      scaledSize: new window.google.maps.Size(45, 45),
                      anchor: new window.google.maps.Point(22.5, 45),
                    }}
                    title={v.vh_muestra || v.vh_patente || 'GPS'}
                  />
                  <OverlayView
                    position={pos}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={() => ({ x: -30, y: -70 })}
                  >
                    <div style={bubbleStyle}>{v.vh_muestra || v.vh_patente || 'GPS'}</div>
                  </OverlayView>
                </React.Fragment>
              );
            })}

          {/* PEDIDOS en Arica */}
          {pedidos
            .filter((p) => (p.campo_yy || '').toLowerCase().includes('arica'))
            .map((p, i) => {
              const pos = getPos(p.campo_geocode) || getPos(`${p.latitud},${p.longitud}`);
              if (!pos) return null;

              const color = getPedidoColor(p);

              return (
                <React.Fragment key={`ar-ped-${p.campo_2}-${i}`}>
                  <OverlayView
                    position={pos}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={() => ({ x: -6, y: -6 })}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPedidoId((prev) => (prev === p.campo_2 ? null : p.campo_2));
                      }}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: color,
                        border: '2px solid #ffffff',
                        boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                        cursor: 'pointer',
                      }}
                    />
                  </OverlayView>

                  {selectedPedidoId === p.campo_2 && (
                    <OverlayView
                      position={pos}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      getPixelPositionOffset={() => ({ x: -60, y: -75 })}
                    >
                      <div style={pedidoBubbleStyle} onClick={(e) => e.stopPropagation()}>
                        <strong>#{p.campo_2}</strong> — {p.campo_8 || 'Pedido'}
                        <br />
                        <strong>Dirección:</strong>{' '}
                        {p.campo_7 || p.direccion || p.campo_3 || p.campo_4 || 'Dirección no disponible'}
                        <br />
                        <strong>Móvil:</strong> {p.campo_11 || '-'} · {p.campo_6 || '-'}
                      </div>
                    </OverlayView>
                  )}
                </React.Fragment>
              );
            })}
        </GoogleMap>

        {/* ================== MAPA ANTOFAGASTA ================== */}
        <GoogleMap
          mapContainerStyle={{ flex: 1 }}
          center={antofaCenter}
          zoom={12}
          options={mapOptions}
          onLoad={(m) => (antofaMapRef.current = m)}
          onIdle={() => {
            if (antofaMapRef.current) setAntofaCenter(antofaMapRef.current.getCenter().toJSON());
          }}
          onClick={() => setSelectedPedidoId(null)}
        >
          {/* GPS externos en Antofagasta (por latitud) */}
          {gpsExternos
            .filter((v) => v.ciudad === 'Antofagasta')
            .map((v, i) => {
              const pos = getPos(v.up_coordenadas);
              if (!pos) return null;

              return (
                <React.Fragment key={`an-gps-${i}`}>
                  <Marker
                    position={pos}
                    icon={{
                      url: v.iconUrl,
                      scaledSize: new window.google.maps.Size(45, 45),
                      anchor: new window.google.maps.Point(22.5, 45),
                    }}
                    title={v.vh_muestra || v.vh_patente || 'GPS'}
                  />
                  <OverlayView
                    position={pos}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={() => ({ x: -30, y: -70 })}
                  >
                    <div style={bubbleStyle}>{v.vh_muestra || v.vh_patente || 'GPS'}</div>
                  </OverlayView>
                </React.Fragment>
              );
            })}

          {/* PEDIDOS en Antofagasta */}
          {pedidos
            .filter((p) => (p.campo_yy || '').toLowerCase().includes('antofagasta'))
            .map((p, i) => {
              const pos = getPos(p.campo_geocode) || getPos(`${p.latitud},${p.longitud}`);
              if (!pos) return null;

              const color = getPedidoColor(p);

              return (
                <React.Fragment key={`an-ped-${p.campo_2}-${i}`}>
                  <OverlayView
                    position={pos}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={() => ({ x: -6, y: -6 })}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPedidoId((prev) => (prev === p.campo_2 ? null : p.campo_2));
                      }}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: color,
                        border: '2px solid #ffffff',
                        boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                        cursor: 'pointer',
                      }}
                    />
                  </OverlayView>

                  {selectedPedidoId === p.campo_2 && (
                    <OverlayView
                      position={pos}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      getPixelPositionOffset={() => ({ x: -60, y: -75 })}
                    >
                      <div style={pedidoBubbleStyle} onClick={(e) => e.stopPropagation()}>
                        <strong>#{p.campo_2}</strong> — {p.campo_8 || 'Pedido'}
                        <br />
                        <strong>Dirección:</strong>{' '}
                        {p.campo_7 || p.direccion || p.campo_3 || p.campo_4 || 'Dirección no disponible'}
                        <br />
                        <strong>Móvil:</strong> {p.campo_11 || '-'} · {p.campo_6 || '-'}
                      </div>
                    </OverlayView>
                  )}
                </React.Fragment>
              );
            })}
        </GoogleMap>
      </div>
    </div>
  );
}

export default GoogleCallCenterMap;
